import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Save, RotateCcw, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { translations as defaultTranslations, LANGUAGES } from '../utils/translations';

// Flatten nested translations to dot-notation keys
// Skips arrays (handled separately) and returns only leaf strings
const flattenKeys = (obj, prefix = '') => {
  const result = [];
  for (const k of Object.keys(obj)) {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    const val = obj[k];
    if (typeof val === 'string') {
      result.push(fullKey);
    } else if (Array.isArray(val)) {
      // Flatten array elements with numeric index
      val.forEach((item, idx) => {
        if (typeof item === 'string') {
          result.push(`${fullKey}.${idx}`);
        }
      });
    } else if (val && typeof val === 'object') {
      result.push(...flattenKeys(val, fullKey));
    }
  }
  return result;
};

// Get value at dot-notation path (supports numeric array indices)
const getByPath = (obj, path) => {
  const keys = path.split('.');
  let cur = obj;
  for (const k of keys) {
    if (cur == null) return undefined;
    cur = cur[k];
  }
  return cur;
};

const TranslationManager = () => {
  const { user } = useAuth();
  const { t, refreshTranslations } = useLanguage();
  const navigate = useNavigate();
  const [overrides, setOverrides] = useState({ cs: {}, pl: {}, de: {}, en: {} });
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  // All flat keys derived once from default Czech translations
  const allKeys = useMemo(() => flattenKeys(defaultTranslations.cs), []);

  // Top-level section list for filter (nav, home, about, services, gallery, contact, footer, admin)
  const sections = useMemo(() => {
    const set = new Set(allKeys.map((k) => k.split('.')[0]));
    return ['all', ...Array.from(set)];
  }, [allKeys]);

  useEffect(() => {
    fetchOverrides();
  }, []);

  const fetchOverrides = async () => {
    try {
      const { data } = await axios.get('/api/translations');
      setOverrides({
        cs: data.cs || {},
        pl: data.pl || {},
        de: data.de || {},
        en: data.en || {},
      });
    } catch (e) {
      toast.error('Chyba při načítání překladů');
    } finally {
      setLoading(false);
    }
  };

  // Current value for a key in a language: edit > override > default
  const getCurrentValue = (lang, key) => {
    const editKey = `${lang}:${key}`;
    if (editKey in edits) return edits[editKey];
    if (key in (overrides[lang] || {})) return overrides[lang][key];
    return getByPath(defaultTranslations[lang], key) ?? '';
  };

  const isOverridden = (lang, key) => key in (overrides[lang] || {});

  const handleChange = (lang, key, value) => {
    setEdits((prev) => ({ ...prev, [`${lang}:${key}`]: value }));
  };

  const handleSaveKey = async (key) => {
    const savingKey = key;
    setSaving((prev) => ({ ...prev, [savingKey]: true }));

    try {
      // Save all 4 language values for this key that have edits
      const promises = [];
      for (const lang of ['cs', 'pl', 'de', 'en']) {
        const editKey = `${lang}:${key}`;
        if (editKey in edits) {
          promises.push(
            axios.put('/api/admin/translations', {
              language: lang,
              key,
              value: edits[editKey],
            })
          );
        }
      }

      if (promises.length === 0) {
        toast.info('Nic nezměněno');
        return;
      }

      await Promise.all(promises);

      // Update overrides locally
      setOverrides((prev) => {
        const next = { ...prev };
        for (const lang of ['cs', 'pl', 'de', 'en']) {
          const editKey = `${lang}:${key}`;
          if (editKey in edits) {
            next[lang] = { ...next[lang], [key]: edits[editKey] };
          }
        }
        return next;
      });

      // Clear edits for this key
      setEdits((prev) => {
        const next = { ...prev };
        for (const lang of ['cs', 'pl', 'de', 'en']) {
          delete next[`${lang}:${key}`];
        }
        return next;
      });

      toast.success('Uloženo');
      // Refresh global translations so changes take effect immediately
      refreshTranslations();
    } catch (e) {
      toast.error('Chyba při ukládání');
    } finally {
      setSaving((prev) => ({ ...prev, [savingKey]: false }));
    }
  };

  const handleRevertKey = async (key) => {
    if (!window.confirm('Vrátit tento klíč na výchozí hodnoty (smazat přepsání)?')) return;

    try {
      const promises = [];
      for (const lang of ['cs', 'pl', 'de', 'en']) {
        if (key in (overrides[lang] || {})) {
          promises.push(
            axios.delete(`/api/admin/translations/${lang}/${key}`)
          );
        }
      }
      await Promise.all(promises);

      setOverrides((prev) => {
        const next = { ...prev };
        for (const lang of ['cs', 'pl', 'de', 'en']) {
          const langOverrides = { ...next[lang] };
          delete langOverrides[key];
          next[lang] = langOverrides;
        }
        return next;
      });

      // Clear edits
      setEdits((prev) => {
        const next = { ...prev };
        for (const lang of ['cs', 'pl', 'de', 'en']) {
          delete next[`${lang}:${key}`];
        }
        return next;
      });

      toast.success('Vráceno na výchozí');
      refreshTranslations();
    } catch (e) {
      toast.error('Chyba při vracení');
    }
  };

  // Apply filters
  const filteredKeys = useMemo(() => {
    let result = allKeys;
    if (sectionFilter !== 'all') {
      result = result.filter((k) => k.startsWith(sectionFilter + '.'));
    }
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((k) => {
        if (k.toLowerCase().includes(s)) return true;
        // Search in default Czech value
        const val = getByPath(defaultTranslations.cs, k);
        if (typeof val === 'string' && val.toLowerCase().includes(s)) return true;
        return false;
      });
    }
    return result;
  }, [allKeys, sectionFilter, search]);

  const hasEditsForKey = (key) =>
    ['cs', 'pl', 'de', 'en'].some((lang) => `${lang}:${key}` in edits);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black px-4 py-8 sm:px-6 lg:px-8" data-testid="translation-manager">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <Link
              to="/admin/dashboard"
              data-testid="back-to-dashboard"
              className="inline-flex items-center text-green-600 dark:text-green-400 hover:underline mb-2 text-sm"
            >
              <ArrowLeft size={16} className="mr-1" />
              Zpět na dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Správa překladů
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
              Upravujte texty webu ve všech 4 jazycích. Změny se projeví okamžitě.
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Hledat klíč nebo text..."
              data-testid="translation-search"
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={sectionFilter}
            onChange={(e) => setSectionFilter(e.target.value)}
            data-testid="translation-section-filter"
            className="px-4 py-2 bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white"
          >
            {sections.map((s) => (
              <option key={s} value={s}>
                {s === 'all' ? 'Všechny sekce' : s}
              </option>
            ))}
          </select>
        </div>

        {/* Translation Table */}
        {loading ? (
          <div className="text-center py-20 text-gray-600 dark:text-gray-400">...</div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Zobrazuji {filteredKeys.length} z {allKeys.length} klíčů
            </div>
            {filteredKeys.map((key) => {
              const overridden = ['cs', 'pl', 'de', 'en'].some((l) => isOverridden(l, key));
              const hasEdit = hasEditsForKey(key);

              return (
                <div
                  key={key}
                  data-testid={`translation-row-${key}`}
                  className={`bg-white dark:bg-gray-900 border rounded-xl p-4 shadow-sm ${
                    hasEdit
                      ? 'border-yellow-400 dark:border-yellow-500'
                      : overridden
                      ? 'border-green-300 dark:border-green-700'
                      : 'border-gray-200 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <code className="text-xs font-mono text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {key}
                    </code>
                    <div className="flex gap-2">
                      {overridden && (
                        <button
                          onClick={() => handleRevertKey(key)}
                          data-testid={`revert-${key}`}
                          className="flex items-center space-x-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-1.5 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                          title="Vrátit na výchozí"
                        >
                          <RotateCcw size={12} />
                          <span>Výchozí</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleSaveKey(key)}
                        disabled={!hasEdit || saving[key]}
                        data-testid={`save-${key}`}
                        className="flex items-center space-x-1 text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 text-white px-3 py-1.5 rounded transition-colors"
                      >
                        <Save size={12} />
                        <span>{saving[key] ? '...' : 'Uložit'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {LANGUAGES.map((lang) => {
                      const value = getCurrentValue(lang.code, key);
                      const isLong = typeof value === 'string' && value.length > 60;
                      return (
                        <div key={lang.code}>
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            {lang.label} – {lang.name}
                          </label>
                          {isLong ? (
                            <textarea
                              value={value}
                              onChange={(e) => handleChange(lang.code, key, e.target.value)}
                              rows={3}
                              data-testid={`trans-input-${lang.code}-${key}`}
                              className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-green-500 resize-none"
                            />
                          ) : (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => handleChange(lang.code, key, e.target.value)}
                              data-testid={`trans-input-${lang.code}-${key}`}
                              className="w-full bg-gray-50 dark:bg-black border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-green-500"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TranslationManager;
